'use client'

import useMediaQuery from '@mui/material/useMediaQuery'
import { useMemo } from 'react'
import { Breakpoint } from './theme'
import {
    breakpoints,
    desktopMediaQueryString,
    largeScreenMediaQueryString,
    mediumScreenMediaQueryString,
    mobileMediaQueryString,
} from './theme/breakpoints'

// This is meant to check for matching media queries outside of a React context or whenever a hook is not desirable
function matchesViewport(query: string): boolean | undefined {
    // Unfortunately the Next build fails if we use `window` because `typeof window` is `"object"` during the build.
    // As of Next 12.2, Sep 2022. See https://github.com/vercel/next.js/issues/36514
    // So instead we test `document` as a hacky way of checking that we're running in a browser.
    if (typeof document === 'undefined') {
        return undefined
    }

    // This check does nothing useful at the moment, but will (hopefully) start becoming meaningful when the above Next issue is resolved
    if (typeof window === 'undefined' || !window.matchMedia) {
        return undefined
    }

    return window.matchMedia(query).matches
}

/**
 * Determines if the viewport fits our mobile breakpoint. Works without React, but should only be used in a browser (e.g. not for server-side rendering).
 */
export function isMobileBrowser(): boolean {
    const isMobileViewport = matchesViewport(mobileMediaQueryString)

    // Even though `screen` represents the monitor size and not the browser size, if the screen width is small enough to be mobile, than the browser must be mobile-sized, too
    if (isMobileViewport === undefined) {
        // Only proceed if the value seems reliable
        if (typeof screen !== 'undefined' && screen.width > 1) {
            return screen.width < breakpoints.sm
        }
    }

    return Boolean(isMobileViewport)
}

type FalsyValue = false | undefined | void | null | 0 | '' | [] | never

function createBreakpointDependentValue(isMobile: boolean, isDesktop: boolean) {
    return function breakpointDependentValue<M, D, F extends FalsyValue = never>(
        valueWhenMobile: M,
        valueWhenDesktop: D,
        valueWhenNotReady: F
    ): M | D | F {
        if (isMobile) {
            return valueWhenMobile
        }

        if (isDesktop) {
            return valueWhenDesktop
        }

        return valueWhenNotReady
    }
}

type WhenBreakpoint = (<T>(callback: () => T) => T) | (() => undefined)

export type UseMediaQueriesHook = {
    isLargeScreen: boolean
    isMediumScreen: boolean
    isReady: boolean
    breakpoint?: Breakpoint

    /**
     * Accepts a callback that is only invoked after we're sure the viewport is mobile-sized
     */
    whenMobile: WhenBreakpoint

    /**
     * Accepts a callback that is only invoked after we're sure the viewport is desktop-sized
     */
    whenDesktop: WhenBreakpoint

    /**
     * Returns different values for mobile and desktop, but only after we're sure about the breakpoint. Until then, a
     * fallback value is returned; the fallback should be something falsy so your component knows that it is not yet safe to use the value.
     *
     * Use this to set a const whose value depends on which media query is matched.
     * @example <div>{breakpointDependentValue('Mobile', 'Desktop', null)}</div>
     */
    breakpointDependentValue: <M, D, F extends FalsyValue>(
        valueWhenMobile: M,
        valueWhenDesktop: D,
        valueWhenNotReady: F
    ) => M | D | F
}

function useMediaQueries(): UseMediaQueriesHook {
    const isMobile = useMediaQuery(mobileMediaQueryString)
    const isDesktop = useMediaQuery(desktopMediaQueryString)
    const isMediumScreen = useMediaQuery(mediumScreenMediaQueryString)
    const isLargeScreen = useMediaQuery(largeScreenMediaQueryString)
    const whenMobile = useMemo(() => (isMobile ? <T>(callback: () => T) => callback() : () => undefined), [isMobile])
    const whenDesktop = useMemo(() => (isDesktop ? <T>(callback: () => T) => callback() : () => undefined), [isDesktop])
    const breakpointDependentValue = createBreakpointDependentValue(isMobile, isDesktop)
    const breakpoint: Breakpoint | undefined = isMobile
        ? 'xs'
        : isDesktop
          ? 'sm'
          : isMediumScreen
            ? 'md'
            : isLargeScreen
              ? 'lg'
              : undefined

    return {
        breakpoint,
        isMediumScreen,
        isLargeScreen,
        isReady: isMobile || isDesktop,
        whenMobile,
        whenDesktop,
        breakpointDependentValue,
    }
}

export default useMediaQueries
